// src/pages/StoneAttributeMasterList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  useGetStoneAttributesQuery,
  useCreateStoneAttributeMutation,
  useUpdateStoneAttributeMutation,
  useDeleteStoneAttributeMutation,
} from "../toolkitServices/stoneAttributeApi";
import {
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiX,
  FiSearch,
  FiPower,
  FiTag,
} from "react-icons/fi";
import Select from "react-select";
import toast from "react-hot-toast";

/* ---------- constants ---------- */
const TYPE_OPTIONS = [
  { value: "shape", label: "Shape" },
  { value: "color", label: "Color" },
  { value: "size", label: "Size" },
  { value: "settingType", label: "Setting Type" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    borderColor: state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #9481ce" : "none",
    "&:hover": { borderColor: "#b2a6e6" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused
      ? "0 0 0 1px rgba(148,129,206,.25)"
      : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const StatusBadge = ({ active }) => (
  <span
    className={[
      "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold select-none",
      active
        ? "border-emerald-100 bg-emerald-50 text-emerald-700"
        : "border-rose-100 bg-rose-50 text-rose-700",
    ].join(" ")}
  >
    {active ? "Active" : "Inactive"}
  </span>
);

/* ---------- page ---------- */
export default function StoneAttributeMasterList() {
  // filters
  const [typeTab, setTypeTab] = useState("shape");
  const [activeOnly, setActiveOnly] = useState("true");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(
    () => ({ type: typeTab, activeOnly, q: debouncedQ, page, limit }),
    [typeTab, activeOnly, debouncedQ, page, limit]
  );

  const { data, isFetching, isError, error } = useGetStoneAttributesQuery(
    params,
    { refetchOnMountOrArgChange: true }
  );

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // mutations
  const [createAttr, { isLoading: creating }] = useCreateStoneAttributeMutation();
  const [updateAttr, { isLoading: updating }] = useUpdateStoneAttributeMutation();
  const [deleteAttr, { isLoading: deleting }] = useDeleteStoneAttributeMutation();

  // modal & local toggling state
  const [openModal, setOpenModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const onAdd = () => {
    setEditRow(null);
    setOpenModal(true);
  };
  const onEdit = (row) => {
    setEditRow(row);
    setOpenModal(true);
  };
  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}" (${row.type})?`)) return;
    try {
      await deleteAttr(row._id).unwrap();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.data?.message || "Delete failed");
    }
  };

  // ✅ Dedicated toggle button handler (status cell is not clickable)
  const onToggleActiveClick = async (row) => {
    setTogglingId(row._id);
    try {
      await updateAttr({ id: row._id, isActive: !row.isActive }).unwrap();
      toast.success(row.isActive ? "Marked inactive" : "Marked active");
    } catch (e) {
      toast.error(e?.data?.message || "Update failed");
    } finally {
      setTogglingId(null);
    }
  };

  // shortcut: Ctrl/Cmd + N to add
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onAdd();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight sm:text-lg">
            <FiTag className="text-sm" />
            Stone Attributes
          </h2>
          <p className="text-[11px] opacity-90">
            Manage Shape • Color • Size • Setting Type
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/90 rounded-md px-2 py-1">
            <FiSearch className="text-[#6f61b2]" />
            <input
              type="text"
              placeholder="Search attribute…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1 rounded-md text-sm text-gray-800 focus:outline-none bg-transparent"
            />
          </div>
          <button
            onClick={onAdd}
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
            title="Ctrl/Cmd + N"
          >
            <FiPlus className="text-[12px]" />
            <span className="hidden sm:inline">Add New</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Tabs + filters bar */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TYPE_OPTIONS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setTypeTab(t.value);
                setPage(1);
              }}
              className={[
                "flex-shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition",
                typeTab === t.value
                  ? "bg-[#9481ce] text-white border-[#9481ce]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-[11px] font-medium text-slate-600">Show</label>
          <select
            value={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
          >
            <option value="true">Active only</option>
            <option value="">All</option>
          </select>
        </div>
        </div>
      </div>

      {/* Table (scrolls inside only) */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[780px] text-left text-xs sm:text-sm">
          <thead className="sticky top-0 z-20 bg-[#efeafd] text-[10px] uppercase text-[#4b3d91] shadow-sm sm:text-[11px]">
            <tr>
              <th className="px-3 py-2 sm:px-4">Type</th>
              <th className="px-3 py-2 sm:px-4">Name</th>
              <th className="px-3 py-2 sm:px-4">Status</th>
              <th className="px-3 py-2 sm:px-4">Created</th>
              <th className="sticky right-0 z-30 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                <span className="sm:hidden">Act</span>
                <span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {/* error */}
            {isError && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-rose-600">
                  {error?.data?.message || "Failed to load"}
                </td>
              </tr>
            )}

            {/* skeleton */}
            {isFetching &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t">
                  <td className="px-4 py-3">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="sticky right-0 z-10 w-[82px] min-w-[82px] border-l border-violet-100 bg-violet-50 px-1 py-3 shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                    <div className="ml-auto h-7 w-20 rounded bg-gray-200 animate-pulse sm:w-24" />
                  </td>
                </tr>
              ))}

            {/* empty */}
            {!isFetching && rows.length === 0 && !isError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No attributes found.
                </td>
              </tr>
            )}

            {/* rows */}
            <AnimatePresence>
              {!isFetching &&
                rows.map((r, idx) => (
                  <motion.tr
                    key={r._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.22, delay: idx * 0.02 }}
                    className="group border-t border-slate-100 transition hover:bg-[#f7f5ff]"
                  >
                    <td className="px-4 py-2 capitalize">
                      {TYPE_OPTIONS.find((t) => t.value === r.type)?.label ||
                        r.type}
                    </td>
                    <td className="px-4 py-2">{r.name}</td>

                    {/* ✅ status is just display now (no click) */}
                    <td className="px-4 py-2">
                      <StatusBadge active={r.isActive} />
                    </td>

                    <td className="px-4 py-2 text-gray-500">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>

                    <td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                      <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onEdit(r)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Edit"
                          aria-label="Edit attribute"
                        >
                          <FiEdit2 />
                        </motion.button>

                        {/* ✅ dedicated activate/deactivate button */}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onToggleActiveClick(r)}
                          disabled={togglingId === r._id || updating}
                          className={[
                            "inline-flex h-6 w-6 items-center justify-center rounded-md border text-[10px] disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]",
                            r.isActive
                              ? "border-amber-100 text-amber-600 hover:bg-amber-50"
                              : "border-emerald-100 text-emerald-600 hover:bg-emerald-50",
                            (togglingId === r._id || updating) && "opacity-60",
                          ].join(" ")}
                          title={r.isActive ? "Deactivate" : "Activate"}
                          aria-label={r.isActive ? "Deactivate attribute" : "Activate attribute"}
                        >
                          {togglingId === r._id ? (
                            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <FiPower />
                          )}
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onDelete(r)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                          title="Delete"
                          aria-label="Delete attribute"
                          disabled={deleting}
                        >
                          <FiTrash2 />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{pages}</span> • {total ?? 0} record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: pages ? page : 1,
                  label: String(pages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, pages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, pages)}</span>
            </span>

            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select
                value={{
                  value: pages ? page : 1,
                  label: String(pages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, pages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[58px]"
                aria-label="Select page number"
              />
              <span>of {Math.max(1, pages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= pages || isFetching}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {openModal && (
          <AttributeModal
            initialType={typeTab}
            editRow={editRow}
            onClose={() => setOpenModal(false)}
            onSubmit={async (payload) => {
              try {
                if (editRow) {
                  await updateAttr({ id: editRow._id, ...payload }).unwrap();
                  toast.success("Updated");
                } else {
                  await createAttr(payload).unwrap();
                  toast.success("Created");
                }
                setOpenModal(false);
              } catch (e) {
                toast.error(e?.data?.message || "Save failed");
              }
            }}
            busy={creating || updating}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- modal ---------- */
function AttributeModal({
  initialType = "shape",
  editRow,
  onClose,
  onSubmit,
  busy,
}) {
  const [type, setType] = useState(
    TYPE_OPTIONS.find((t) => t.value === (editRow?.type || initialType))
  );
  const [name, setName] = useState(editRow?.name || "");
  const [isActive, setIsActive] = useState(
    typeof editRow?.isActive === "boolean" ? editRow.isActive : true
  );

  const valid = type && name.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40">
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-3 bg-[#9481ce] text-white flex items-center justify-between">
          <h3 className="text-base font-semibold">
            {editRow ? "Edit Attribute" : "Add Attribute"}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20"
            title="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <Select
              isClearable={false}
              options={TYPE_OPTIONS}
              styles={selectStyles}
              value={type}
              onChange={(opt) => setType(opt)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g., Round / F / 1.5mm / Bezel"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor="active" className="text-sm">
              Active
            </label>
          </div>
        </div>

        <div className="px-5 pb-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-gray-200 text-gray-800"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSubmit({ type: type.value, name: name.trim(), isActive })
            }
            className="px-4 py-2 rounded-md bg-[#9481ce] text-white disabled:opacity-60"
            disabled={!valid || busy}
          >
            {editRow ? "Update" : "Create"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
